<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import VipBadge from 'dashboard/components-next/VipBadge.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Avatar,
    VipBadge,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
    VoiceCallButton,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    channelType: {
      type: String,
      default: '',
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['panelClose', 'openLiveChatForms'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showMergeModal: false,
      showDeleteModal: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      selectedChat: 'getSelectedChat',
    }),
    conversationChannelType() {
      return String(
        this.channelType || this.selectedChat?.meta?.channel || ''
      ).toLowerCase();
    },
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    isLiveChatConversation() {
      return (
        this.conversationChannelType.includes('webwidget') ||
        this.conversationChannelType.includes('web_widget')
      );
    },
    isLivechatNameLocked() {
      const lockedValue =
        this.contact.additional_attributes?.wlab_livechat_name_lock?.locked;
      return this.contact.livechat_name_locked === true
        ? true
        : lockedValue === true || lockedValue === 'true';
    },
    livechatNameLockTooltip() {
      return this.isLivechatNameLocked
        ? 'Unlock livechat name'
        : 'Lock livechat name';
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    customAttributes() {
      return this.contact.custom_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;
      return {
        twitter: twitterScreenName,
        telegram: telegramUsername,
        ...(socialProfiles || {}),
      };
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    openComposeConversationModal(toggleFn) {
      toggleFn();
      // Flag to prevent triggering drag n drop,
      // When compose modal is active
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
    },
    closeComposeConversationModal() {
      // Flag to enable drag n drop,
      // When compose modal is closed
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    openLiveChatFormsModal() {
      this.$emit('openLiveChatForms');
    },
    async toggleLivechatNameLock() {
      if (
        !this.isLiveChatConversation ||
        !this.contact?.id ||
        this.uiFlags.isUpdating
      ) {
        return;
      }

      try {
        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          lockLivechatName: !this.isLivechatNameLocked,
        });
      } catch (error) {
        useAlert(
          error?.message || 'Unable to update the livechat name lock right now.'
        );
      }
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
  },
};
</script>

<template>
  <div>
    <div class="wlab-profile-hero">
      <div v-if="showAvatar" class="wlab-profile-hero__avatar">
        <Avatar
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="64"
          hide-offline-status
          rounded-full
        />
        <VipBadge
          :vip-level="contact.custom_attributes?.vip"
          size="md"
          position="top-left"
          :offset-x="-8"
          :offset-y="-12"
        />
      </div>

      <span class="wlab-profile-hero__badge">
        {{ customAttributes.vip || 'Contact' }}
      </span>

      <div class="wlab-profile-hero__name-row">
        <p class="wlab-profile-hero__name">{{ contact.name }}</p>
        <NextButton
          v-if="isLiveChatConversation"
          v-tooltip.top-end="livechatNameLockTooltip"
          type="button"
          icon="i-ph-check-bold"
          color="teal"
          size="xs"
          :variant="isLivechatNameLocked ? 'solid' : 'faded'"
          class="contact-name-lock-btn"
          :disabled="uiFlags.isUpdating"
          @click="toggleLivechatNameLock"
        />
      </div>
      <p class="wlab-profile-hero__sub">
        {{
          additionalAttributes.description ||
          contact.email ||
          contact.phone_number ||
          '—'
        }}
      </p>

      <div class="wlab-profile-actions">
        <ComposeConversation
          :contact-id="String(contact.id)"
          is-modal
          @close="closeComposeConversationModal"
        >
          <template #trigger="{ toggle }">
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chats-circle-bold"
              slate
              faded
              sm
              class="contact-action-btn"
              @click="openComposeConversationModal(toggle)"
            />
          </template>
        </ComposeConversation>
        <VoiceCallButton
          :contact-id="String(contact.id)"
          :phone="contact.phone_number"
          icon="i-ph-phone-bold"
          size="sm"
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
          slate
          faded
          class="contact-action-btn"
        />
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple-bold"
          slate
          faded
          sm
          class="contact-action-btn"
          @click="toggleEditModal"
        />
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
          icon="i-ph-users-three-bold"
          slate
          faded
          sm
          class="contact-action-btn"
          :disabled="uiFlags.isMerging"
          @click="openMergeModal"
        />
        <NextButton
          v-if="isAdmin"
          v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
          icon="i-ph-trash-bold"
          slate
          faded
          sm
          ruby
          class="contact-action-btn"
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
        <NextButton
          v-if="isLiveChatConversation"
          v-tooltip.top-end="$t('CONTACT_PANEL.LIVE_CHAT_FORMS.TOOLTIP')"
          icon="i-ph-list-checks-bold"
          slate
          faded
          sm
          class="contact-action-btn"
          @click="openLiveChatFormsModal"
        />
        <a
          :href="contactProfileLink"
          target="_blank"
          rel="noopener nofollow noreferrer"
          class="contact-action-btn inline-flex"
          title="Open in contacts"
        >
          <span class="i-lucide-external-link text-sm text-n-slate-10" />
        </a>
      </div>
    </div>

    <div style="padding: 12px 16px">
      <div class="wlab-profile-card wlab-contact-info-card">
        <!-- Conversation ID -->
        <div v-if="conversationId" class="wlab-info-row">
          <div class="wlab-info-row__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="14"
              height="14"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              />
            </svg>
          </div>
          <div class="wlab-info-row__body">
            <div class="wlab-info-row__label">
              {{ $t('CONTACT_PANEL.CONVERSATION_ID') }}
            </div>
            <div class="wlab-info-row__value">#{{ conversationId }}</div>
          </div>
        </div>

        <!-- Email -->
        <div class="wlab-info-row">
          <div class="wlab-info-row__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="14"
              height="14"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
              />
              <polyline
                points="22,6 12,13 2,6"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
              />
            </svg>
          </div>
          <div class="wlab-info-row__body">
            <div class="wlab-info-row__label">
              {{ $t('CONTACT_PANEL.EMAIL_ADDRESS') }}
            </div>
            <div class="wlab-info-row__value">
              <a v-if="contact.email" :href="`mailto:${contact.email}`">{{
                contact.email
              }}</a>
              <span v-else class="wlab-info-row__muted">{{
                $t('CONTACT_PANEL.NOT_AVAILABLE')
              }}</span>
            </div>
          </div>
        </div>

        <!-- Phone -->
        <div class="wlab-info-row">
          <div class="wlab-info-row__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="14"
              height="14"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.45 2 2 0 0 1 3.56 1.27h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.81a16 16 0 0 0 6.29 6.29l.91-.9a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21.83 16z"
              />
            </svg>
          </div>
          <div class="wlab-info-row__body">
            <div class="wlab-info-row__label">
              {{ $t('CONTACT_PANEL.PHONE_NUMBER') }}
            </div>
            <div class="wlab-info-row__value">
              <a
                v-if="contact.phone_number"
                :href="`tel:${contact.phone_number}`"
                >{{ contact.phone_number }}</a
              >
              <span v-else class="wlab-info-row__muted">{{
                $t('CONTACT_PANEL.NOT_AVAILABLE')
              }}</span>
            </div>
          </div>
        </div>

        <!-- Identifier -->
        <div v-if="contact.identifier" class="wlab-info-row">
          <div class="wlab-info-row__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="14"
              height="14"
            >
              <rect
                x="2"
                y="5"
                width="20"
                height="14"
                rx="2"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
              />
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 10h.01M16 14h.01M8 10h4M8 14h4"
              />
            </svg>
          </div>
          <div class="wlab-info-row__body">
            <div class="wlab-info-row__label">
              {{ $t('CONTACT_PANEL.IDENTIFIER') }}
            </div>
            <div class="wlab-info-row__value">{{ contact.identifier }}</div>
          </div>
        </div>

        <!-- VIP — always shown -->
        <div class="wlab-info-row">
          <div class="wlab-info-row__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="14"
              height="14"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5z"
              />
            </svg>
          </div>
          <div class="wlab-info-row__body">
            <div class="wlab-info-row__label">
              {{ $t('CONTACT_PANEL.VIP_LEVEL') }}
            </div>
            <div class="wlab-info-row__value">
              <span v-if="customAttributes.vip">{{
                customAttributes.vip
              }}</span>
              <span v-else class="wlab-info-row__muted">{{
                $t('CONTACT_PANEL.NOT_AVAILABLE')
              }}</span>
            </div>
          </div>
        </div>

        <!-- Location -->
        <div
          v-if="location || additionalAttributes.location"
          class="wlab-info-row"
        >
          <div class="wlab-info-row__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="14"
              height="14"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
              />
              <circle
                cx="12"
                cy="9"
                r="2.5"
                stroke="currentColor"
                stroke-width="2"
              />
            </svg>
          </div>
          <div class="wlab-info-row__body">
            <div class="wlab-info-row__label">
              {{ $t('CONTACT_PANEL.LOCATION') }}
            </div>
            <div
              v-dompurify-html="location || additionalAttributes.location"
              class="wlab-info-row__value"
            />
          </div>
        </div>

        <!-- Social -->
        <div
          v-if="Object.values(socialProfiles).some(Boolean)"
          class="wlab-info-row"
        >
          <div class="wlab-info-row__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="14"
              height="14"
            >
              <circle
                cx="18"
                cy="5"
                r="3"
                stroke="currentColor"
                stroke-width="2"
              />
              <circle
                cx="6"
                cy="12"
                r="3"
                stroke="currentColor"
                stroke-width="2"
              />
              <circle
                cx="18"
                cy="19"
                r="3"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-width="2"
                d="m8.59 13.51 6.83 3.98M15.41 6.51 8.59 10.49"
              />
            </svg>
          </div>
          <div class="wlab-info-row__body">
            <div class="wlab-info-row__label">Social</div>
            <div class="wlab-info-row__value">
              <SocialIcons :social-profiles="socialProfiles" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <EditContact
      v-if="showEditModal"
      :show="showEditModal"
      :contact="contact"
      :channel-type="channelType || selectedChat?.meta?.channel || ''"
      @cancel="toggleEditModal"
    />
    <ContactMergeModal
      v-if="showMergeModal"
      :primary-contact="contact"
      :show="showMergeModal"
      @close="closeMergeModal"
    />

    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>

<style scoped>
/* Custom icon size for contact action buttons */
.contact-action-btn :deep(span[class*='i-ph-']) {
  width: 11px !important;
  height: 11px !important;
  font-size: 11px !important;
}

.wlab-profile-hero__name-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 100%;
}

.contact-name-lock-btn {
  min-width: 22px !important;
  width: 22px !important;
  height: 22px !important;
  min-height: 22px !important;
  padding: 0 !important;
  flex-shrink: 0;
}

.contact-name-lock-btn :deep(span[class*='i-ph-']) {
  width: 10px !important;
  height: 10px !important;
  font-size: 10px !important;
}
</style>
